import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';

const useTransportationBookings = () => {
    const { authorizedFetch } = useAuth();

    const [bookings, setBookings] = useState([]);
    const [scheduled, setScheduled] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchAll = async () => {
        if (!authorizedFetch) return;
        setLoading(true);
        try {
            const res = await authorizedFetch(`${API_URL}/api/transportation`);
            if (!res.ok) throw new Error(`Failed to fetch: ${res.statusText}`);
            const data = await res.json();
            setBookings(data.bookings || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchScheduled = async () => {
        if (!authorizedFetch) return;
        try {
            const res = await authorizedFetch(`${API_URL}/api/transportation/scheduled`);
            if (!res.ok) throw new Error(`Failed to fetch scheduled: ${res.statusText}`);
            setScheduled(await res.json());
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchHistory = async () => {
        if (!authorizedFetch) return;
        try {
            const res = await authorizedFetch(`${API_URL}/api/transportation/history`);
            if (!res.ok) throw new Error(`Failed to fetch history: ${res.statusText}`);
            setHistory(await res.json());
        } catch (err) {
            setError(err.message);
        }
    };

    const createBooking = async (data) => {
        if (!authorizedFetch) throw new Error('Not authenticated');
        setLoading(true);
        try {
            const res = await authorizedFetch(`${API_URL}/api/transportation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || res.statusText);
            }
            const result = await res.json();
            await Promise.all([fetchAll(), fetchScheduled(), fetchHistory()]);
            return result.booking;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const updateBooking = async (id, data) => {
        if (!authorizedFetch) throw new Error('Not authenticated');
        setLoading(true);
        try {
            const res = await authorizedFetch(`${API_URL}/api/transportation/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || res.statusText);
            }
            const result = await res.json();
            await Promise.all([fetchAll(), fetchScheduled(), fetchHistory()]);
            return result.booking;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const deleteBooking = async (id) => {
        if (!authorizedFetch) throw new Error('Not authenticated');
        setLoading(true);
        try {
            const res = await authorizedFetch(`${API_URL}/api/transportation/${id}`, { method: 'DELETE' });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || res.statusText);
            }
            await Promise.all([fetchAll(), fetchScheduled(), fetchHistory()]);
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authorizedFetch) {
            Promise.all([fetchAll(), fetchScheduled(), fetchHistory()]);
        }
    }, [authorizedFetch]);

    useEffect(() => {
        if (error) {
            const t = setTimeout(() => setError(''), 5000);
            return () => clearTimeout(t);
        }
    }, [error]);

    return {
        bookings,
        scheduled,
        history,
        loading,
        error,
        createBooking,
        updateBooking,
        deleteBooking,
        fetchAll,
        fetchScheduled,
        fetchHistory,
        clearError: () => setError('')
    };
};

export default useTransportationBookings;
